// How each `TaskFailureKind` is BUDGETED — the attempt ladder, and the three
// predicates every failure lane reads, projected from each kind's row
// (`./failure-kinds/`). See `../task.ts` for the file docblock.

import { trustedQuotaReset } from '../../value-objects/llm-credential';
import { projectFailureKinds, type TaskFailureKind, TaskFailureKindSchema } from './failure-kind';
import { TaskBudgetKind, TaskExhaustionAction } from './failure-kinds/row';
import type { TaskHoldReason } from './hold';

// The ladder vocabulary and the park clocks live beside the rows that are written in
// them (`./failure-kinds/row.ts`).
export {
  BOX_BASELINE_RED_BACKOFF_BASE_MS,
  BOX_BASELINE_RED_BACKOFF_CAP_MS,
  NO_SCREEN_KEY_RECHECK_MS,
  PROVIDER_CAPACITY_RECHECK_MS,
  PROVIDER_KEY_REFUSED_RECHECK_MS,
  PROVIDER_TRANSIENT_BACKOFF_BASE_MS,
  PROVIDER_TRANSIENT_BACKOFF_CAP_MS,
  REVIEW_NO_MODEL_RECHECK_MS,
  SCREEN_UNAVAILABLE_RECHECK_MS,
  TASK_MAX_ATTEMPTS,
  TaskBudgetKind,
  TaskExhaustionAction,
} from './failure-kinds/row';

/**
 * How long a box that refused a task for a red baseline is EXCLUDED from claiming
 * that task again (#3094). Another box may claim it meanwhile; the refusing box may
 * claim it again once the window lapses.
 *
 * THE FAILURE IT PREVENTS: a task whose only capable box refused it stays pending
 * forever. So the exclusion lapses on a CLOCK rather than on an eligibility re-check
 * ("is there another box that could take it?") — a re-check is a second predicate
 * that can drift from the claim gate's own, and a drift there holds the row against
 * its one capable box with nothing ever clearing it. The clock always clears.
 */
export const BOX_FAULT_EXCLUSION_MINUTES = 30;

export type TaskFailureBudget =
  | {
      readonly kind: typeof TaskBudgetKind.Charged;
      /** Attempts this kind may spend. `1` ⇒ terminal on the first occurrence. */
      readonly maxAttempts: number;
      readonly onExhaustion: TaskExhaustionAction;
    }
  | {
      readonly kind: typeof TaskBudgetKind.Uncharged;
      /**
       * How many CONSECUTIVE uncharged refusals of this kind end the task (#3094),
       * or absent for a kind with no such bound. Not an attempt ladder: nothing is
       * charged, and the count is of refusals in a row, not of runs spent. Absent is
       * a decision per kind — `provider_rate_limit` is paced by the box cooldown and
       * must stay unbounded — never a default.
       */
      readonly maxConsecutive?: number;
      readonly streakNeutral?: never;
    }
  | {
      readonly kind: typeof TaskBudgetKind.Uncharged;
      /**
       * An OUTAGE OF THE ACCOUNT'S KEYS, not a refusal of the task (2026-10-18,
       * `screen_key_unusable`): the failure write keeps `uncharged_streak` exactly as it
       * was, so a run of outages neither counts toward another kind's `maxConsecutive`
       * nor breaks one. Without it, a day-long outage left the streak in the dozens and
       * the first ordinary refusal after the key came back ended the task at once —
       * the outage spending the task one failure late. Never with `maxConsecutive`: an
       * outage's only bound is its park, which a usable key releases.
       */
      readonly streakNeutral: true;
      readonly maxConsecutive?: never;
    }
  | { readonly kind: typeof TaskBudgetKind.Terminal };

/**
 * ONE BUDGET PER FAILURE KIND — slice 11 Part A, replacing a single
 * {@link TASK_MAX_ATTEMPTS} that covered every cause alike.
 *
 * Projected from each kind's row (`./failure-kinds/`), total over the closed enum (not a
 * partial map): `FailureKindRow.budget` is required, so a new
 * {@link TaskFailureKind} fails typecheck at its row until someone DECIDES its budget
 * AND its exhaustion action, which is the fail-fast-on-unknown rule made
 * structural. It is the successor of `TASK_FAILURE_RETRYABLE`, which answered
 * only "retry?" and therefore could not express the one distinction the
 * evidence demanded: a failure that is retryable AND must not be charged.
 *
 * | kind | budget | on exhaustion |
 * |---|---|---|
 * | `runner_error` (clone, OOM, adapter timeout) | 3, charged | fail |
 * | `provider_error` (5xx) | 3, charged | fail |
 * | `provider_rate_limit` (account condition) | NOT CHARGED | re-pend HELD (`provider_capacity`) |
 * | `provider_transient` (5xx / 429 past the in-step retries) | NOT CHARGED | re-pend HELD (`provider_capacity`): stated reset, else streak backoff (#6501) |
 * | `box_baseline_red` (the base is red) | NOT CHARGED, uncapped | re-pend HELD (`repo_env_fault`) on a streak backoff (F7) |
 * | `disk_floor_unmet` (the box's hygiene floor) | NOT CHARGED, 3 consecutive | `refusals_exhausted` (#4309) |
 * | `pool_key_spent` (this key's wallet, a sibling to rotate to) | NOT CHARGED, 3 consecutive | `refusals_exhausted` |
 * | `survey_empty` (the scout's survey answered nothing) | 3, charged | fail |
 * | `max_output_unknown` (no sourced output ceiling) | 1, charged | fail |
 * | `review_no_model` (the review had no model) | NOT CHARGED | re-pend HELD (`review_awaiting_model`) |
 * | `screen_unavailable` (the screen did not answer, the pool still serves) | NOT CHARGED, 3 consecutive | re-pend HELD (`provider_capacity`), then `refusals_exhausted` |
 * | `screen_key_unusable` (the screen did not answer, no key can serve it) | NOT CHARGED, uncapped, streak-neutral | re-pend HELD (`no_screen_key`) |
 * | `provider_quota_exhausted` / `provider_auth_invalid` (the account's key refused) | NOT CHARGED | re-pend HELD (`provider_key_refused`) |
 * | `error_unclassified` | 1, charged | fail |
 * | `gate_unrunnable` (the gate reached no verdict on this box) | 3, charged | HOLD (2026-09-29) |
 * | `stuck_silent` (reaper re-pend) | 3, charged | fail |
 * | `dependency_failed` | terminal | never retried |
 *
 * ON EXHAUSTION. `hold` on the kinds whose every attempt died without the change ever
 * being graded AND on the box — `gate_unrunnable` today; the enumeration is
 * {@link taskFailureHoldsOnExhaustion}'s. Every other charged kind is `fail`: the task
 * ends with its ladder.
 */
export const TASK_FAILURE_BUDGET: Record<TaskFailureKind, TaskFailureBudget> = projectFailureKinds(
  (row) => row.budget,
);

/** Where each kind's re-pend parks, or `null` — the row's `park` (`FailureKindPark`). */
const TASK_FAILURE_PARK = projectFailureKinds((row) => row.park);

/** The hold a re-pend of this kind lands on, and for how long. */
export type TaskRependHold = {
  readonly reason:
    | typeof TaskHoldReason.ReviewAwaitingModel
    | typeof TaskHoldReason.ProviderKeyRefused
    | typeof TaskHoldReason.ProviderCapacity
    | typeof TaskHoldReason.RepoEnvFault
    | typeof TaskHoldReason.PrQuietWindow
    | typeof TaskHoldReason.CiPending
    | typeof TaskHoldReason.NoScreenKey;
  readonly holdMs: number;
  /**
   * The provider's STATED reset the hold ends on (plan 102 slice 03 R4), present
   * only when one was given and {@link trustedQuotaReset} trusts it; `holdMs` is
   * then its distance from the caller's `now`. The failure write binds THIS
   * instant, so the stored `hold_until` is the provider's time, not a re-derived
   * one. Absent ⇒ the flat recheck clock — or, with {@link backoffCapMs}, the backoff.
   */
  readonly holdUntil?: Date;
  /**
   * Present only on an UNSTATED park of a kind that backs off (`provider_transient`):
   * `holdMs` is then the FIRST window, doubled per consecutive uncharged failure up to
   * this ceiling. Only the failure write knows the streak, so it derives the window.
   */
  readonly backoffCapMs?: number;
};

/** A provider's stated reset for the refused key, and the clock it is judged against. */
export type StatedKeyReset = { readonly holdUntil: Date; readonly now: Date };

/**
 * Does a re-pend of this kind land HELD rather than immediately claimable? The ONE
 * predicate the failure write reads (`failOrRetryTask`), the way
 * {@link taskFailureBoxLocal} is for the box exclusion. So "which kinds wait for a
 * key" is a property of each kind's row (its `park`), never a literal in SQL.
 *
 * Enumerated. Every kind that WAITS FOR A KEY parks, and only those:
 *   - `review_no_model` goes to `review_awaiting_model`: the review had no model at all.
 *   - `provider_quota_exhausted` and `provider_auth_invalid` go to
 *     `provider_key_refused`: the provider refused the key the run held (plan 101 L3).
 *   - `provider_rate_limit` goes to `provider_capacity`: the pool refused the run for
 *     CAPACITY, not the key — so it is a DIFFERENT hold from the key park, woken by
 *     the pool's recovery window, never by a key change. An immediate re-pend here is
 *     the hot loop this arm closes (one task, 83 runs, attempt 0 throughout).
 *   - `repo_env_down` goes to `repo_env_fault`: not a key at all — the repo's own setup
 *     is down on every box, and the park waits for the sweep that releases it.
 *   - `human_pushing` goes to `pr_quiet_window`: not a key either — a person is pushing to the
 *     babysit round's pull request, and the park ends on the quiet instant the box stated
 *     (`../babysit/human-quiet`), never re-claimed while they are still at it.
 *   - `review_ci_pending` goes to `ci_pending`: a review box read the head's CI still pending at claim,
 *     and the park ends on the CI-ceiling instant the box stated (`reviewCiPendingMessage`),
 *     else `CI_WAIT_CEILING_MS` from now; a CI completion on the head releases it sooner (#6718).
 *   - `screen_unavailable` goes to `provider_capacity`: the screen could not answer on a
 *     pool that still serves (a timeout, a fenced request), so the park waits
 *     `SCREEN_UNAVAILABLE_RECHECK_MS` — never the 1-2 s re-claim loop it replaced
 *     (2026-10-09). It parked on `no_screen_key` until 2026-10-19 and the row said "no
 *     screen key" on accounts with working ones; the keyless cause is `screen_key_unusable`.
 *   - `provider_transient` goes to `provider_capacity` too (#6501): an outage the box's
 *     retries outlasted. A dated reset ends the park there; an undated one backs off,
 *     doubling with the uncharged streak (`backoffCapMs`), because the box's own pull hold
 *     paced only that box and every other box re-claimed the row at once.
 *   - `screen_key_unusable` goes to `no_screen_key`, released by a key turning `valid`
 *     (`releaseHoldsOnKeyValid`) or `SCREEN_UNAVAILABLE_RECHECK_MS`: the mothership's regrade
 *     of a `screen_unavailable` whose account had no key the screen could use as the
 *     failure landed. Uncapped and streak-neutral, so an outage of the account's keys never
 *     ends the task.
 *   - `box_baseline_red` goes to `repo_env_fault` (F7, 2026-10-19): the base is red, which is
 *     the repository's state on every box, never the task's. The window backs off on the
 *     uncharged streak (`backoffCapMs`) from `BOX_BASELINE_RED_BACKOFF_BASE_MS`; the cap of
 *     three in a row it replaced failed 26 tesote.com tasks on 2026-10-05..07.
 * `null` for every other kind: `pool_key_spent` rotates onto a sibling key through
 * the task's skip set, and every remaining kind is a ladder.
 *
 * `stated` (plan 102 slice 03 R4): the provider's own reset for the refused key.
 * When {@link trustedQuotaReset} trusts it, the `provider_key_refused` park ends
 * THERE instead of on {@link PROVIDER_KEY_REFUSED_RECHECK_MS} — a daily wall no
 * longer re-probes 6 h early, a weekly one no longer 28 times. Untrusted (past,
 * or beyond `QUOTA_RESET_MAX_MS`) is treated as unstated, the key park's rule.
 * The capacity park reads it too — the pool's quoted reset IS its recovery window,
 * ending on it instead of {@link PROVIDER_CAPACITY_RECHECK_MS}; the review park
 * never does: it waits on a model, not a key's window.
 */
export function taskFailureRependHold(
  kind: TaskFailureKind,
  stated?: StatedKeyReset,
): TaskRependHold | null {
  const park = TASK_FAILURE_PARK[kind];
  if (park === null) return null;
  const until =
    park.honorsStatedReset && stated ? trustedQuotaReset(stated.holdUntil, stated.now) : null;
  if (until !== null && stated) {
    return {
      reason: park.reason,
      holdMs: until.getTime() - stated.now.getTime(),
      holdUntil: until,
    };
  }
  return park.backoffCapMs === undefined
    ? { reason: park.reason, holdMs: park.backstopMs }
    : { reason: park.reason, holdMs: park.backstopMs, backoffCapMs: park.backoffCapMs };
}

/**
 * The kinds whose cause is THE BOX rather than the work — the condition that
 * produced the failure is still true on that box a minute later, so the re-pend
 * must send the task somewhere else ({@link BOX_FAULT_EXCLUSION_MINUTES}).
 *
 * Declared per kind (the row's `boxLocal`) rather than a literal at the write site: the
 * exclusion shipped (#3094) spread on `box_baseline_red` alone, and the next
 * box-local kind inherited "any box may reclaim it" silently. Setting the flag on
 * the kind's row is the whole change.
 *
 * MEASURED, 2026-09-19, tesote's fleet. One box carried a leftover compose
 * stack — project `tesoteai`, `working_dir` `/tmp/gatecheck/vendor/docker`, up
 * SIX DAYS — holding `127.0.0.1:6382`. The repo's own `./bin/setup` could not
 * bind its Dragonfly there, so every task that landed on that box died
 * `gate_unrunnable` before any agent ran, and the box reported healthy
 * throughout. One task (`tsk_c36e8680…`) died that way twice: each re-pend
 * cleared `runner_id`, and with no exclusion the same box was free to reclaim
 * it. The leftover is invisible to hygiene by design — an unconfined compose
 * stack is reported, never reaped (`apps/runner/src/hygiene/act.ts`
 * `containerRemoval`) — so "wait for the box to recover" has no mechanism
 * behind it here.
 *
 * NOR THE THREE KINDS NAMED IN THE SAME CHANGE, each refused for its own reason
 * rather than as a batch. `pool_key_spent`: the fault is the CREDENTIAL, and the
 * rotation is the task's key skip set, so any box resolves onto a sibling — an
 * exclusion would shrink the pool for nothing. `max_output_unknown`: every box
 * reads the same model registry, so the condition is not box-local at all.
 * `survey_empty`: #2954 reads it as a box state and that reading is a HYPOTHESIS —
 * the same silence is what a repo with nothing citable, or a model declining every
 * scout question, produces. This set takes measured evidence per kind; the read
 * that would settle it is that kind's rows grouped by `runner_id`, one box
 * carrying the population versus an even spread.
 */
const BOX_LOCAL = projectFailureKinds((row) => row.boxLocal);
const BOX_LOCAL_FAILURE_KINDS: ReadonlySet<TaskFailureKind> = new Set(
  TaskFailureKindSchema.options.filter((kind) => BOX_LOCAL[kind]),
);

/**
 * Must a re-pend of this kind exclude the box that produced it? The ONE
 * predicate the failure write reads, so "which kinds are the box's fault" is a
 * property of {@link BOX_LOCAL_FAILURE_KINDS} rather than a literal in SQL.
 */
export function taskFailureBoxLocal(kind: TaskFailureKind): boolean {
  return BOX_LOCAL_FAILURE_KINDS.has(kind);
}

/**
 * Does a failure of this kind spend one of the task's attempts? The ONE
 * predicate both failure lanes read, so "an account condition is never charged"
 * is a property of this table rather than a rule restated at each write site.
 */
export function taskFailureCharged(kind: TaskFailureKind): boolean {
  return TASK_FAILURE_BUDGET[kind].kind === TaskBudgetKind.Charged;
}

/**
 * May a failure of this kind ever re-enter the queue? `terminal` never can;
 * `charged` can while its ladder holds; `uncharged` always can.
 */
export function taskFailureRetryable(kind: TaskFailureKind): boolean {
  return TASK_FAILURE_BUDGET[kind].kind !== TaskBudgetKind.Terminal;
}

/**
 * The ladder a charged kind spends, or `null` for a kind that spends none.
 * `null` rather than `0`/`Infinity`: the uncharged and terminal arms have no
 * ladder, and a sentinel number is how a caller ends up comparing against one.
 */
export function taskFailureMaxAttempts(kind: TaskFailureKind): number | null {
  const budget = TASK_FAILURE_BUDGET[kind];
  return budget.kind === TaskBudgetKind.Charged ? budget.maxAttempts : null;
}

/**
 * Does a spent CHARGED ladder of this kind HOLD the task instead of ending it
 * (`TaskExhaustionAction.Hold`, 2026-09-29)? The ONE predicate the failure write reads
 * (`failOrRetryTask`), so "which exhaustions are the fleet's and not the work's" is a
 * property of the kind's row, never a literal at the write site.
 *
 * THE PROPERTY a `hold` row must have, pinned by `env-fault-hold.test.ts`: charged, AND
 * `blamesBox` (an operator should inspect the box), AND `boxLocal` (the re-pend already
 * leaves that box), AND `babysitRound: uncharged` (the babysit ladder already excuses it
 * as "nothing about the change was tried"), AND neither parkable nor resume-parkable. Three
 * separate rulings on the row already say the death is no evidence about the work; `hold`
 * is the fourth, and the guard refuses a `hold` row that disagrees with any of them.
 *
 * THE ENUMERATION of charged kinds carrying all three flags, each decided:
 *   - `gate_unrunnable` — HOLD. The repo's gate never reached a verdict on the box (it
 *     could not execute, refused with exit 75, or found no cage — the box's own memory
 *     line moved to `gate_memory_unmet`, #6347, whose uncharged budget can never hold
 *     by the predicate below). Its row's own copy: "the change was never judged".
 *   - `assignment_undecodable` — stays `fail`. The box never ran the task because its
 *     build could not decode the push; a push NO box decodes is a fact about the
 *     task's envelope as much as the fleet, and its row states `Fail` for that reason
 *     (plan 101 F4). No model session is spent on it either (the box never started a
 *     run), so its ladder bills nothing — the cost `hold` exists to stop is absent.
 * The guard fails when a NEW charged kind gains all three flags, so the next one is
 * decided here rather than inherited.
 *
 * NOT HERE, and why: the UNCHARGED environment kinds with a consecutive cap
 * (`disk_floor_unmet`, `gate_memory_unmet`, `service_unreachable`,
 * `task_cage_unmintable`, `baseline_stash_stranded`) end on `refusals_exhausted`, a
 * different ladder (#3094) whose arm carries no exhaustion action. `box_baseline_red` was
 * one of them (15 tasks ended `failed box_baseline_red` in the 30 days to 2026-09-29) and
 * left the arm on 2026-10-19 (F7): it now parks uncapped on its re-pend instead. The charged kinds that blame the box but not
 * box-locally (`runner_error`, `stuck_silent`, `error_unclassified`, `tool_unavailable`)
 * cover the WORK's failures too — their babysit round is charged or their ladder is 1 —
 * so a spent ladder there is evidence, and they keep their class.
 */
export function taskFailureHoldsOnExhaustion(kind: TaskFailureKind): boolean {
  const budget = TASK_FAILURE_BUDGET[kind];
  return (
    budget.kind === TaskBudgetKind.Charged && budget.onExhaustion === TaskExhaustionAction.Hold
  );
}
